import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { COMPANY } from "../data/site";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FLAGS = [
  { code: "zm", name: "Zambia" },
  { code: "in", name: "India" },
  { code: "cn", name: "China" },
  { code: "ae", name: "United Arab Emirates" },
  { code: "za", name: "South Africa" },
  { code: "cd", name: "DR Congo" },
  { code: "tz", name: "Tanzania" },
  { code: "kr", name: "South Korea" },
  { code: "jp", name: "Japan" },
];

const inputCls =
  "w-full border border-zinc-700 bg-surface px-4 py-3 text-sm text-zinc-100 placeholder-zinc-600 outline-none transition-colors duration-300 focus:border-copper";

export const ContactSection = () => {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", country: "", product: "Coal", quantity: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent. We reply within 24 hours.");
      setForm({ name: "", company: "", email: "", phone: "", country: "", product: "Coal", quantity: "", message: "" });
    } catch {
      toast.error("Could not send. Please email or WhatsApp us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="border-t border-zinc-800 bg-surface" data-testid="contact-section">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-16 px-6 py-28 lg:grid-cols-2 lg:px-12 lg:py-40">
        <Reveal>
          <p className="font-mono-label text-xs text-copper">04 — Talk to us</p>
          <h2 className="font-display mt-6 text-4xl font-semibold uppercase leading-tight text-zinc-100 sm:text-5xl">
            Move coal<br />before it's <span className="text-copper">gone.</span>
          </h2>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-zinc-400 md:text-base">
            Tell us the product, grade and tonnage you need. We respond to every serious buyer within 24 hours.
          </p>
          <div className="mt-10 space-y-4">
            <a href={`mailto:${COMPANY.email}`} data-testid="contact-email-link" className="block font-mono text-sm text-zinc-300 transition-colors duration-300 hover:text-copper-light">
              {COMPANY.email}
            </a>
            <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="flex items-center gap-3 font-mono text-sm text-zinc-300 transition-colors duration-300 hover:text-copper-light">
              <img src="https://flagcdn.com/w40/zm.png" alt="Zambia flag" className="h-4 w-6 object-cover" />
              {COMPANY.phone} — WhatsApp (Zambia)
            </a>
            <a href={COMPANY.whatsapp2} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link-2" className="flex items-center gap-3 font-mono text-sm text-zinc-300 transition-colors duration-300 hover:text-copper-light">
              <img src="https://flagcdn.com/w40/in.png" alt="India flag" className="h-4 w-6 object-cover" />
              {COMPANY.phone2} — WhatsApp (India)
            </a>
            <p className="font-mono text-sm text-zinc-500">{COMPANY.location}</p>
          </div>

          <div className="mt-14">
            <p className="font-stencil text-[10px] text-copper">Shipping worldwide</p>
            <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="world-flags">
              {FLAGS.map((f) => (
                <img
                  key={f.code}
                  src={`https://flagcdn.com/w80/${f.code}.png`}
                  alt={`${f.name} flag`}
                  title={f.name}
                  className="h-6 w-9 border border-zinc-800 object-cover transition-transform duration-300 hover:scale-110"
                />
              ))}
            </div>
            <p className="mt-4 font-mono text-[10px] leading-relaxed text-zinc-600">
              Home base: Zambia · Serving bulk buyers across Africa, Asia & the Gulf
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="enquiry-form">
            <input required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="enquiry-name-input" />
            <input placeholder="Company" value={form.company} onChange={set("company")} className={inputCls} data-testid="enquiry-company-input" />
            <input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} data-testid="enquiry-email-input" />
            <input placeholder="Phone / WhatsApp" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="enquiry-phone-input" />
            <input placeholder="Country" value={form.country} onChange={set("country")} className={inputCls} data-testid="enquiry-country-input" />
            <select value={form.product} onChange={set("product")} className={inputCls} data-testid="enquiry-product-select">
              <option>Coal</option>
              <option>Copper Ingots</option>
              <option>Aluminium Ingots</option>
              <option>Other</option>
            </select>
            <input placeholder="Quantity (e.g. 5,000 MT)" value={form.quantity} onChange={set("quantity")} className={`${inputCls} sm:col-span-2`} data-testid="enquiry-quantity-input" />
            <textarea rows={4} placeholder="Message — grade, destination port, terms…" value={form.message} onChange={set("message")} className={`${inputCls} sm:col-span-2`} data-testid="enquiry-message-input" />
            <button
              type="submit"
              disabled={sending}
              data-testid="enquiry-submit-btn"
              className="mt-2 bg-copper px-8 py-4 font-mono-label text-xs text-ink transition-colors duration-300 hover:bg-copper-light disabled:opacity-50 sm:col-span-2 sm:w-fit"
            >
              {sending ? "Sending…" : "Send Enquiry"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
